/**
 * A new challenge (ADR 0076): a named colleague or anyone, a pace, a side, rated or not.
 *
 * The paces offered are the type's own (`time_controls`); clock games use the ADR's
 * presets, nothing under three minutes.
 */
import { useEffect, useId, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Field, Input, Select, Switch } from "@/core/ui";
import type { Side } from "@/duels/registry";
import {
  searchOpponents,
  useCreateDuel,
  type DuelGame,
  type Person,
  type TimeControl,
} from "./api";
import { useTimeControlLabel } from "./format";

const CLOCK_PRESETS: TimeControl[] = [
  { kind: "clock", base_s: 180, increment_s: 2 },
  { kind: "clock", base_s: 300, increment_s: 3 },
  { kind: "clock", base_s: 600, increment_s: 5 },
  { kind: "clock", base_s: 900, increment_s: 10 },
];
const CORRESPONDENCE_DAYS = [1, 2, 3, 5, 7];

function presetsFor(game: DuelGame): TimeControl[] {
  const out: TimeControl[] = [];
  if (game.time_controls.includes("clock")) out.push(...CLOCK_PRESETS);
  if (game.time_controls.includes("correspondence"))
    out.push(...CORRESPONDENCE_DAYS.map((days): TimeControl => ({ kind: "correspondence", days })));
  if (game.time_controls.includes("realtime")) out.push({ kind: "realtime" });
  return out;
}

export function ChallengeForm({ game, ns }: { game: DuelGame; ns: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const label = useTimeControlLabel();
  const create = useCreateDuel();
  const id = useId();
  const presets = presetsFor(game);
  const [who, setWho] = useState<"colleague" | "anyone">("colleague");
  const [opponent, setOpponent] = useState<Person | null>(null);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Person[]>([]);
  const [preset, setPreset] = useState(0);
  const [side, setSide] = useState<Side | "random">("random");
  const [rated, setRated] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const q = query.trim();
    if (who !== "colleague" || q.length < 2 || opponent?.name === q) {
      setResults([]);
      return;
    }
    let live = true;
    const timer = window.setTimeout(() => {
      searchOpponents(q)
        .then((found) => live && setResults(found.results))
        .catch(() => live && setResults([]));
    }, 200);
    return () => {
      live = false;
      window.clearTimeout(timer);
    };
  }, [query, who, opponent]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError("");
    if (who === "colleague" && !opponent) {
      setError(t("duels.form.pick_opponent"));
      return;
    }
    create.mutate(
      {
        game_key: game.key,
        opponent_id: who === "colleague" ? opponent!.id : null,
        time_control: presets[preset],
        side,
        rated,
      },
      {
        onSuccess: (duel) => navigate(`/duels/${game.key}/${duel.id}`),
        onError: (failure) => {
          const code = (failure as { payload?: { code?: string } }).payload?.code;
          setError(code === "throttled" ? t("duels.form.too_many") : t("duels.form.failed"));
        },
      },
    );
  };

  return (
    <form className="bt-duel-form" onSubmit={submit} aria-labelledby={`${id}-title`}>
      <h2 id={`${id}-title`} className="bt-duel-lobby__section-title">
        {t("duels.form.title")}
      </h2>
      <fieldset className="bt-duel-form__who">
        <legend className="bt-label">{t("duels.form.opponent")}</legend>
        <label className="bt-duel-form__choice">
          <input
            type="radio"
            name={`${id}-who`}
            checked={who === "colleague"}
            onChange={() => setWho("colleague")}
          />
          {t("duels.form.colleague")}
        </label>
        <label className="bt-duel-form__choice">
          <input
            type="radio"
            name={`${id}-who`}
            checked={who === "anyone"}
            onChange={() => setWho("anyone")}
          />
          {t("duels.form.anyone")}
        </label>
      </fieldset>
      {who === "colleague" ? (
        <div className="bt-duel-form__search">
          <Field label={t("duels.form.search")} htmlFor={`${id}-q`}>
            <Input
              id={`${id}-q`}
              value={query}
              autoComplete="off"
              placeholder={t("duels.form.search_placeholder")}
              onChange={(e) => {
                setQuery(e.target.value);
                setOpponent(null);
              }}
            />
          </Field>
          {results.length > 0 ? (
            <ul className="bt-duel-form__results" aria-label={t("duels.form.results")}>
              {results.map((person) => (
                <li key={person.id}>
                  <button
                    type="button"
                    className="bt-duel-form__result"
                    onClick={() => {
                      setOpponent(person);
                      setQuery(person.name);
                      setResults([]);
                    }}
                  >
                    {person.name}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          {opponent ? (
            <p className="bt-duel-form__picked" role="status">
              {t("duels.form.picked", { name: opponent.name })}
            </p>
          ) : null}
        </div>
      ) : (
        <p className="bt-field__hint">{t("duels.form.anyone_hint")}</p>
      )}
      <div className="bt-duel-form__row">
        <Field label={t("duels.form.pace")} htmlFor={`${id}-pace`}>
          <Select
            id={`${id}-pace`}
            value={preset}
            onChange={(e) => setPreset(Number(e.target.value))}
          >
            {presets.map((control, index) => (
              <option key={index} value={index}>
                {label(control)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t("duels.form.side")} htmlFor={`${id}-side`}>
          <Select
            id={`${id}-side`}
            value={side}
            onChange={(e) => setSide(e.target.value as Side | "random")}
          >
            <option value="random">{t("duels.form.random")}</option>
            <option value="white">{t("sides.white", { ns })}</option>
            <option value="black">{t("sides.black", { ns })}</option>
          </Select>
        </Field>
      </div>
      <p className="bt-field__hint">{t(`duels.form.hint_${presets[preset].kind}`)}</p>
      <Switch
        id={`${id}-rated`}
        checked={rated}
        onChange={setRated}
        label={t("duels.form.rated")}
      />
      {error ? (
        <p className="bt-duel-form__error" role="alert">
          {error}
        </p>
      ) : null}
      <div className="bt-duel-form__actions">
        <Button type="submit" loading={create.isPending}>
          {t("duels.form.send")}
        </Button>
      </div>
    </form>
  );
}
